import React, { useEffect } from 'react';
import { Form } from 'formik';
import FormInput from '../common/FormInput.tsx';
import RequiredLabel from '../common/RequiredLabel.tsx';
import { AppDispatch, RootState } from '../../store/store.ts';
import { useDispatch, useSelector } from 'react-redux';

import { getAllClients } from '../../slices/ClientSlice.ts';
import { fetchServices } from '../../slices/ServiceMasterSlice.ts';
import { fetchSubServices } from '../../slices/SubServiceMasterSlice.ts';

const RequestForm = ({ handleChange, values, touched, errors, setFieldValue }) => {
  const dispatch = useDispatch<AppDispatch>();

  const { clients = [] } = useSelector((state: RootState) => state.clientSlice) || {};
  const { services = [] } = useSelector((state: RootState) => state.serviceMaster) || {};
  const { subServices = [] } = useSelector((state: RootState) => state.subServiceMaster) || {};

  useEffect(() => {
    // Fetch all data with pagination parameters
    dispatch(getAllClients({
      offset: 0,
      limit: 100,
      sortField: "",
      sortOrder: null,
      searchInput: ""
    }));
    dispatch(fetchServices({
      offset: 0,
      limit: 100,
      sortField: "",
      sortOrder: null,
      searchInput: ""
    }));
    dispatch(fetchSubServices({
      offset: 0,
      limit: 100,
      sortField: "",
      sortOrder: null,
      searchInput: ""
    }));
  }, [dispatch]);

  // Ensure we have arrays even if the data is not loaded yet
  const clientOptions = Array.isArray(clients) 
    ? clients
        .filter(client => client?.id != null)
        .map(client => ({
          value: client.id,
          label: client.name || '',
          key: client.id
        }))
    : [];

  const serviceOptions = Array.isArray(services)
    ? services
        .filter(service => service?.id != null)
        .map(service => ({
          value: service.id,
          label: service.name || '',
          key: service.id
        }))
    : [];

  const subServiceOptions = Array.isArray(subServices)
    ? subServices
        .filter(subService => subService?.service_id === values.service_id && subService?.id != null)
        .map(subService => ({
          value: subService.id,
          label: subService.subservice_name || '',
          key: subService.id
        }))
    : [];

  const handleClientChange = (value: number) => {
    if (!value) return;
    const selectedClient = clients?.find(client => client?.id === value);
    setFieldValue('customer_id', value);
    setFieldValue('customer_name', selectedClient?.name || '');
  };

  const handleServiceChange = (value: number) => {
    if (!value) return;
    const selectedService = services?.find(service => service?.id === value);
    setFieldValue('service_id', value);
    setFieldValue('service_name', selectedService?.name || '');
    // Clear subservice when service changes
    setFieldValue('sub_service_id', '');
    setFieldValue('sub_service_name', '');
  };

  const handleSubServiceChange = (value: number) => {
    if (!value) return;
    const selectedSubService = subServices?.find(subService => subService?.id === value);
    setFieldValue('sub_service_id', value);
    setFieldValue('sub_service_name', selectedSubService?.subservice_name || '');
  };

  return (
    <Form>
      <p className='text-[#0F44BE] mb-3'>CLIENT INFORMATION</p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <RequiredLabel>Client Name</RequiredLabel>
          <FormInput
            name="customer_id"
            type="select"
            placeholder="Select client name"
            options={clientOptions}
            onChange={handleClientChange}
            value={values.customer_id || ''}
            error={touched.customer_id && errors.customer_id}
          />
        </div>

        <div>
          <RequiredLabel>Care Category</RequiredLabel>
          <FormInput
            name="service_id"
            type="select"
            placeholder="Select service name"
            options={serviceOptions}
            onChange={handleServiceChange}
            value={values.service_id || ''}
            error={touched.service_id && errors.service_id}
          />
        </div>

        <div>
          <RequiredLabel>Care Type</RequiredLabel>
          <FormInput
            name="sub_service_id"
            type="select"
            placeholder="Select subservice name"
            options={subServiceOptions}
            onChange={handleSubServiceChange}
            value={values.sub_service_id || ''}
            error={touched.sub_service_id && errors.sub_service_id}
            disabled={!values.service_id}
          />
        </div>
      </div>
    </Form>
  );
};

export default RequestForm;
